import { Dialog } from '@headlessui/react';
import { Fragment, useState, useEffect } from 'react';
import clsx from 'clsx';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { saveEmployeeSchedule } from '../api/scheduleApi';

dayjs.extend(customParseFormat);

export default function ScheduleModal({ employee, date, onClose, businessId }) {
  const [start, setStart]   = useState('09:00');
  const [end, setEnd]       = useState('17:00');
  const [breakStart, setBS] = useState('');
  const [breakEnd, setBE]   = useState('');
  const [error, setError]   = useState('');

  /* ---------- validation ---------- */
  const validate = () => {
    const s = dayjs(start, 'HH:mm', true);
    const e = dayjs(end,   'HH:mm', true);
    if (!s.isValid() || !e.isValid())         return 'Invalid time values';
    if (!s.isBefore(e))                       return 'Start must be before end';

    const hasBreak = breakStart || breakEnd;
    if (hasBreak && !(breakStart && breakEnd))
      return 'Provide both break start and break end';

    if (hasBreak) {
      const bs = dayjs(breakStart, 'HH:mm', true);
      const be = dayjs(breakEnd,   'HH:mm', true);
      if (!bs.isValid() || !be.isValid())     return 'Invalid break time values';
      if (!s.isBefore(bs) || !bs.isBefore(be) || !be.isBefore(e))
        return 'Break must be within shift and start before end';
    }
    return '';
  };

  useEffect(() => { setError(validate()); }, [start, end, breakStart, breakEnd]);

  /* ---------- save ---------- */
  const submit = async () => {
    if (error) return;
    try {
      await saveEmployeeSchedule(businessId, {
        employee_id: employee.id,
        schedule   : {
          [dayjs(date).format('YYYY-MM-DD')]: [{
            start,
            end,
            breaks: (breakStart && breakEnd) ? [{ start: breakStart, end: breakEnd }] : [],
          }],
        },
      });
      onClose();
      window.dispatchEvent(new Event('schedule-updated'));
    } catch (e) {
      alert('Failed to save schedule');
      console.error(e);
    }
  };

  /* ---------- ui ---------- */
  return (
    <Dialog open onClose={onClose} as={Fragment}>
      <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
        <Dialog.Panel className="bg-white p-6 rounded-lg w-full max-w-md space-y-4">
          <Dialog.Title className="text-lg font-medium">
            Add shift – {employee.full_name}&nbsp;({dayjs(date).format('DD MMM YYYY')})
          </Dialog.Title>

          <div className="grid grid-cols-2 gap-3 text-sm items-center">
            <label>Start time</label>
            <input type="time" value={start} onChange={e => setStart(e.target.value)} className="border p-1 rounded"/>

            <label>End time</label>
            <input type="time" value={end}   onChange={e => setEnd(e.target.value)}   className="border p-1 rounded"/>

            <label>Break start</label>
            <input type="time" value={breakStart} onChange={e => setBS(e.target.value)} className="border p-1 rounded"/>

            <label>Break end</label>
            <input type="time" value={breakEnd}   onChange={e => setBE(e.target.value)} className="border p-1 rounded"/>
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex justify-end space-x-2 pt-2">
            <button onClick={onClose} className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300">Cancel</button>
            <button
              disabled={!!error}
              onClick={submit}
              className={clsx(
                'px-3 py-1 rounded text-white',
                error ? 'bg-gray-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700',
              )}
            >
              Save
            </button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
